import {useCallback, useEffect, useRef, useState} from "react";
import i18n from "../../i18n";
import {useTranslation} from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type {SmsSenderRegistration, SmsSenderRegistrationRequestStatus} from "@calendall/shared";
import {
    createSmsSenderRegistration,
    getSmsSenderRegistrations,
    updateSmsSenderRegistration,
} from "../../api/smsSenderApi";
import {isValidCountryCode, isValidSenderId} from "./smsSenderValidation";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {SectionCard} from "../ui/SectionCard";
import {StatusChip} from "../ui/StatusChip";
import {colors, fonts, type ChipTone} from "../../theme/tokens";

const REGISTRATION_TONES: Record<SmsSenderRegistration["status"], ChipTone> = {
    submitted: "info",
    approved: "success",
    rejected: "error",
    retired: "neutral",
};

const REQUEST_STATUSES: SmsSenderRegistrationRequestStatus[] = ["submitted", "approved", "rejected"];

type DialogState =
        | {mode: "create"}
        | {mode: "edit"; registration: SmsSenderRegistration};

export function smsSenderStatusLabelKey(status: SmsSenderRegistration["status"]): string {
    return `SMS_SENDER_STATUS_${status.toUpperCase()}`;
}

type SmsSenderRegistrationsCardProps = {
    tenantId: string;
    number?: number;
    id?: string;
    /** Starts collapsed behind an Expand control; the content stays mounted so loading is not deferred. */
    collapsible?: boolean;
};

export function SmsSenderRegistrationsCard({tenantId, number, id, collapsible = false}: SmsSenderRegistrationsCardProps) {
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState(!collapsible);
    const [registrations, setRegistrations] = useState<SmsSenderRegistration[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [dialog, setDialog] = useState<DialogState | null>(null);

    // Only the latest request may update the list; an earlier one answering late would show stale data.
    const latestRequest = useRef(0);

    const load = useCallback(() => {
        const request = ++latestRequest.current;
        getSmsSenderRegistrations(tenantId)
                .then((result) => {
                    if (request === latestRequest.current) {
                        setRegistrations(result);
                        setError(null);
                    }
                })
                .catch((e: unknown) => {
                    if (request === latestRequest.current) {
                        setError(e instanceof Error ? e.message : i18n.t("SMS_SENDER_REGISTRATIONS_LOAD_ERROR"));
                    }
                });
    }, [tenantId]);

    useEffect(load, [load]);

    return (
            <SectionCard
                    id={id}
                    number={number}
                    title={t("SMS_SENDER_REGISTRATIONS")}
                    flush
                    collapsed={!expanded}
                    helper={registrations ? t("SMS_SENDER_REGISTRATIONS_COUNT", {count: registrations.length}) : undefined}
                    actions={
                        collapsible ? (
                                <Button
                                        variant="text"
                                        onClick={() => setExpanded((open) => !open)}
                                        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        aria-expanded={expanded}
                                >
                                    {expanded ? t("COLLAPSE") : t("EXPAND")}
                                </Button>
                        ) : undefined
                    }
            >
                <Collapse in={expanded} data-testid="sms-sender-registrations">
                <Box sx={{p: 2}}>
                {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
                {registrations && registrations.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                            {t("SMS_SENDER_REGISTRATIONS_EMPTY")}
                        </Typography>
                )}
                {registrations && registrations.length > 0 && (
                        <TableContainer sx={{mb: 2, border: `1px solid ${colors.cardBorder}`, borderRadius: "4px"}}>
                            <Table size="small" aria-label={t("SMS_SENDER_REGISTRATIONS")}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t("SMS_SENDER_COUNTRY")}</TableCell>
                                        <TableCell>{t("SMS_SENDER_ID")}</TableCell>
                                        <TableCell>{t("SMS_SENDER_STATUS")}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {registrations.map((registration) => (
                                            <TableRow key={registration.id}>
                                                <TableCell>{registration.country}</TableCell>
                                                <TableCell sx={{fontFamily: fonts.mono, fontSize: "12px"}}>{registration.senderId}</TableCell>
                                                <TableCell>
                                                    <StatusChip tone={REGISTRATION_TONES[registration.status]}>
                                                        {t(smsSenderStatusLabelKey(registration.status))}
                                                    </StatusChip>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {registration.status === "submitted" && (
                                                            <Button
                                                                    size="small"
                                                                    variant="text"
                                                                    onClick={() => setDialog({mode: "edit", registration})}
                                                                    aria-label={t("SMS_SENDER_EDIT_REGISTRATION", {senderId: registration.senderId})}
                                                            >
                                                                {t("SMS_SENDER_EDIT")}
                                                            </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                )}
                <Button variant="outlined" onClick={() => setDialog({mode: "create"})}>
                    {t("SMS_SENDER_ADD_REGISTRATION")}
                </Button>
                {dialog && (
                        <RegistrationDialog
                                tenantId={tenantId}
                                state={dialog}
                                onClose={() => setDialog(null)}
                                onSaved={() => {
                                    setDialog(null);
                                    load();
                                }}
                        />
                )}
                </Box>
                </Collapse>
            </SectionCard>
    );
}

function RegistrationDialog({tenantId, state, onClose, onSaved}: {
    tenantId: string;
    state: DialogState;
    onClose: () => void;
    onSaved: () => void;
}) {
    const {t} = useTranslation();
    const [country, setCountry] = useState(state.mode === "edit" ? state.registration.country : "");
    const [senderId, setSenderId] = useState(state.mode === "edit" ? state.registration.senderId : "");
    const [status, setStatus] = useState<SmsSenderRegistrationRequestStatus>("submitted");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const countryInvalid = !isValidCountryCode(country);
    const senderIdInvalid = !isValidSenderId(senderId);

    async function save() {
        setSaving(true);
        setError(null);
        try {
            if (state.mode === "create") {
                await createSmsSenderRegistration(tenantId, {country, senderId, status});
            } else {
                await updateSmsSenderRegistration(tenantId, state.registration.id, {senderId, status});
            }
            onSaved();
        } catch (e) {
            setError(e instanceof Error ? e.message : t("SMS_SENDER_REGISTRATION_SAVE_ERROR"));
            setSaving(false);
        }
    }

    return (
            <Dialog open onClose={onClose} fullWidth maxWidth="xs">
                <DialogTitle>
                    {state.mode === "create" ? t("SMS_SENDER_ADD_REGISTRATION") : t("SMS_SENDER_EDIT_REGISTRATION_TITLE")}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{display: "flex", flexDirection: "column", gap: 2, pt: 1}}>
                        {error && <Alert severity="error">{error}</Alert>}
                        {status === "approved" && (
                                <Alert severity="info">{t("SMS_SENDER_APPROVAL_RETIRES_PREVIOUS")}</Alert>
                        )}
                        <TextField
                                size="small"
                                label={t("SMS_SENDER_COUNTRY")}
                                value={country}
                                onChange={(e) => setCountry(e.target.value.toUpperCase())}
                                disabled={state.mode === "edit"}
                                error={country.length > 0 && countryInvalid}
                                helperText={t("SMS_SENDER_COUNTRY_HINT")}
                                inputProps={{maxLength: 2}}
                        />
                        <TextField
                                size="small"
                                label={t("SMS_SENDER_ID")}
                                value={senderId}
                                onChange={(e) => setSenderId(e.target.value)}
                                error={senderId.length > 0 && senderIdInvalid}
                                helperText={t("SMS_SENDER_ID_RULES")}
                        />
                        <FormControl size="small">
                            <InputLabel id="sms-sender-status-label">{t("SMS_SENDER_STATUS")}</InputLabel>
                            <Select<SmsSenderRegistrationRequestStatus>
                                    labelId="sms-sender-status-label"
                                    label={t("SMS_SENDER_STATUS")}
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                            >
                                {REQUEST_STATUSES.map((option) => (
                                        <MenuItem key={option} value={option}>{t(smsSenderStatusLabelKey(option))}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={onClose}>{t("CANCEL")}</Button>
                    <Button
                            variant="contained"
                            onClick={() => void save()}
                            disabled={countryInvalid || senderIdInvalid || saving}
                    >
                        {saving ? t("DETAIL_SAVING") : t("DETAIL_SAVE")}
                    </Button>
                </DialogActions>
            </Dialog>
    );
}
